import { FormEvent, useEffect, useState } from "react";
import { api, AgentNumber, SipConfigView, StaffRecord } from "../lib/api";

async function copyText(value: string) {
  await navigator.clipboard.writeText(value);
}

export function AgentTelephonyPage({ csrf }: { csrf: string }) {
  const [staff, setStaff] = useState<StaffRecord | null>(null);
  const [numbers, setNumbers] = useState<AgentNumber[]>([]);
  const [sip, setSip] = useState<SipConfigView | null>(null);
  const [defaultId, setDefaultId] = useState("");
  const [method, setMethod] = useState<"SIP" | "PERSONAL_PHONE">("SIP");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");

  async function load() {
    const data = await api.agentTelephony();
    setStaff(data.staff);
    setNumbers(data.numbers || []);
    setSip(data.sip);
    setDefaultId(data.routing?.defaultOutboundNumberId || data.numbers?.[0]?.id || "");
    setMethod((data.routing?.selectedReceivingMethod as "SIP" | "PERSONAL_PHONE") || "SIP");
    setPhone(data.staff?.forwardingNumber || "");
  }

  useEffect(() => {
    load().catch((e) => setError(e instanceof Error ? e.message : "Failed to load telephony"));
  }, []);

  async function run(label: string, fn: () => Promise<void>) {
    setBusy(label);
    setError("");
    setMsg("");
    try {
      await fn();
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Request failed");
    } finally {
      setBusy("");
    }
  }

  async function savePhone(e: FormEvent) {
    e.preventDefault();
    await run("save", async () => {
      await api.saveForwardingNumber(phone, csrf);
      setCodeSent(false);
      setCode("");
      setMsg("Number saved. It is not verified until you complete the SMS code.");
    });
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>My telephony</h1>
          <p>Your assigned Twilio numbers, FalconPhone SIP identity, and personal forwarding number.</p>
        </div>
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      {msg ? <div className="alert success">{msg}</div> : null}

      <div className="card">
        <h2>My numbers</h2>
        {numbers.length === 0 ? <p className="muted">No Twilio numbers are assigned to you yet.</p> : (
          <ul>
            {numbers.map((n) => (
              <li key={n.id}><strong>{n.displayName || n.label || n.phone}</strong> <span className="mono">{n.phone || n.e164}</span></li>
            ))}
          </ul>
        )}
        <label>
          Default calling number
          <select value={defaultId} onChange={(e) => setDefaultId(e.target.value)}>
            <option value="">Select</option>
            {numbers.map((n) => <option key={n.id} value={n.id}>{n.displayName || n.phone}</option>)}
          </select>
        </label>
        <label>
          Receive transfers on
          <select value={method} onChange={(e) => setMethod(e.target.value as "SIP" | "PERSONAL_PHONE")}>
            <option value="SIP">FalconPhone</option>
            <option value="PERSONAL_PHONE" disabled={!staff?.forwardingNumberVerified}>Personal phone {staff?.forwardingNumberVerified ? "" : "(verify first)"}</option>
          </select>
        </label>
        <button className="btn" type="button" disabled={Boolean(busy)} onClick={() => run("route", async () => {
          await api.saveAgentRouting({ defaultOutboundNumberId: defaultId, selectedReceivingMethod: method }, csrf);
          setMsg("Call routing saved.");
        })}>Save routing</button>
      </div>

      <div className="card">
        <h2>My SIP</h2>
        {sip?.sipUsername ? (
          <>
            <p>SIP domain: <span className="mono">{sip.sipDomain}</span></p>
            <p>SIP username: <span className="mono">{sip.sipUsername}</span></p>
            <p>SIP password: <span className="mono">{sip.sipPassword}</span></p>
            <p>Port: {sip.sipPort} · Transport: {String(sip.sipTransport || "").toUpperCase()}</p>
            <div className="row-actions">
              <button className="btn sm ghost" type="button" onClick={() => copyText(sip.sipUsername || "")}>Copy username</button>
              <button className="btn sm ghost" type="button" onClick={() => copyText(sip.sipDomain || "")}>Copy domain</button>
              <button className="btn sm" type="button" onClick={() => run("sip", async () => {
                const r = await api.agentSipConfig(csrf, true);
                setSip(r.config);
                if (r.config.copyFullConfig) await copyText(r.config.copyFullConfig);
                setMsg("Full SIP config copied. Do not share it.");
              })}>Copy full config</button>
            </div>
          </>
        ) : <p className="muted">No SIP account is assigned yet. An admin assigns a Twilio number to provision SIP automatically.</p>}
      </div>

      <div className="card">
        <h2>Personal call forwarding</h2>
        <form onSubmit={savePhone} className="stack">
          <label>
            Phone
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1XXXXXXXXXX" />
          </label>
          <p>Status: {staff?.forwardingNumberVerified ? <span className="badge active">VERIFIED</span> : <span className="badge">NOT VERIFIED</span>}</p>
          <div className="row-actions">
            <button className="btn ghost" type="submit" disabled={Boolean(busy)}>Save number</button>
            <button className="btn" type="button" disabled={Boolean(busy) || !staff?.forwardingNumber} onClick={() => run("otp", async () => {
              await api.sendForwardingCode(csrf);
              setCodeSent(true);
              setMsg("Verification code sent.");
            })}>{busy === "otp" ? "Sending…" : "Send verification code"}</button>
          </div>
        </form>
        {codeSent && !staff?.forwardingNumberVerified ? (
          <div className="stack" style={{ marginTop: "1rem" }}>
            <label>
              Verification code
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" />
            </label>
            <button className="btn success" type="button" disabled={Boolean(busy) || !code} onClick={() => run("verify", async () => {
              await api.verifyForwardingCode(code, csrf);
              setMsg("Personal number verified.");
              setCode("");
              setCodeSent(false);
            })}>Verify</button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
